import SDK from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_SDK'
import nim_server_config from './nim_server_conf'
import { getToken, getSingleList, getGroupList, groupDetail } from "@/im/api"
import Vue from 'vue'
import cloneDeep from "lodash/cloneDeep"
import { BehaviorSubject } from 'rxjs'

const appKey = process.env.VUE_APP_IM_KEY

const IMEvents = {
  setAuthor: 'setAuthor',
  connectLoading: 'connectLoading',

  initSingle: 'initSingle',
  dismissSingle: 'dismissSingle',
  
  initTeams: 'initTeams',
  teamsUpdate: 'teamsUpdate',
  dismissTeam: 'dismissTeam',

  updateSession: 'updateSession',

  onmsg: 'onmsg',
  messageUpdated: 'messageUpdated',
  messageLoading: 'messageLoading',
  messageSendError: 'messageSendError',
}

class IMClient extends Vue {
  constructor() {
    super()
    this.EVENTS = IMEvents
    this._userInfo = {}
    this._nim = null
    this._singleMap = {}
    this._teamsMap = {}
    this._msgMap = {}
    this._sessionList = []
    this._lastMsgMap = {}
    this._initIm()
  }

  async _initIm() {
    try {
      const tokenRes = await getToken()
      this._userInfo = tokenRes.data
      if(tokenRes) {
        this._getSingleList()
      }
      this._nim = SDK.NIM.getInstance({
        debug: process.env.NODE_ENV === 'development',
        appKey: appKey,
        account: this._userInfo.accId,
        token: this._userInfo.token,
        authType: 0,
        db: true,
        privateConf: nim_server_config,

        // connect
        needReconnect: true,
        quickReconnect: true,
        onconnect: this._onconnect.bind(this),
        ondisconnect: this._onDisConnect.bind(this),

        // single
        onsyncfriendaction: this._getSingleList.bind(this),

        // teams
        syncTeams: false,
        onCreateTeam: this._updateTeams.bind(this),
        onUpdateTeam: this._updateTeams.bind(this),
        onupdateteammember: this._updateTeams.bind(this),
        onDismissTeam: this._dismissTeam.bind(this),
        onAddTeamMembers: this._updateTeamMembers.bind(this),
        onRemoveTeamMembers: this._updateTeamMembers.bind(this),

        // msg
        rollbackDelMsgUnread: true,
        syncRoamingMsgs: true,
        onmsg: this._onmsg.bind(this),
        onroamingmsgs: this._updateMsg.bind(this),
        onofflinemsgs: this._updateMsg.bind(this),
        
        // session
        syncSessionUnread: true,
        onsessions: this._onsessions.bind(this),
        onupdatesession: this._onsessions.bind(this),

        onsyncdone: this._onsyncdone.bind(this),
      })
      window._nim = this._nim
    } catch (err) {
      console.error('im init', err)
      this.$emit(IMEvents.connectLoading, false)
    }
  }
  // connect
  async _onconnect() {
    this.$emit(IMEvents.setAuthor, this._userInfo.accId)
    try {
      await this._getSingleList()
      await this._getTeamsList()
    } catch(err) {
      console.log(err)
    }
    this.$emit(IMEvents.connectLoading, false)
  }
  _onDisConnect(error) {
    const { code } = error;
    console.error('im disconnect', error)
    switch(code) {
      case 302:
        this._reconnect()
        break;
      case 417:
        break;
      case 'kicked':
        break;
      default:
        break;
    }
  }
  async _reconnect() {
    try {
      this.$emit(IMEvents.connectLoading, true)
      const tokenRes = await getToken()
      this._userInfo = tokenRes.data.data
      this._nim.setOptions({
        token: this._userInfo.token
      })
      this._nim.connect()
    } catch(err) {
      console.error('im reconnect', err)
      this.$emit(IMEvents.connectLoading, false)
    }
  }

  // single
  async _getSingleList() {
    try {
      const list = (await getSingleList()).data || []
      let map = {}
      list.forEach(item => {
        if (item.accId) map[item.accId] = item
      })
      this._singleMap = map;
      this.$emit(IMEvents.initSingle, list)
    } catch(err) {
      console.log('getSingleList error ', err)
    }
  }
  _dismissSingle(accId) {
    delete this._singleMap[accId]
    this.$emit(IMEvents.dismissSingle)
  }


  // teams
  async _getTeamsList() {
    const list = (await getGroupList()).data || []
    let map = {}
    list.forEach(item => {
      if (item.tid) map[item.tid] = item
    })
    this._teamsMap = map
    this.$emit(IMEvents.initTeams, list)
  }
  _dismissTeam({ teamId }) {
    delete this._teamsMap[teamId]
    this.$emit(IMEvents.dismissTeam, teamId)
  }
  _updateTeamMembers(info) {
    this.getTeamDetail(info.team.teamId)
  }
  _updateTeams(team) {
    this.getTeamDetail(team.teamId)
  }
  async getTeamDetail(tid) {
    try {
      const detail = (await groupDetail({ tid })).data || {}
      this._teamsMap[tid] = { ...(this._teamsMap[tid] || {}), ...detail }
      this.$emit(IMEvents.teamsUpdate, this._teamsMap[tid])
    } catch(err) {
      console.error('get team detail err', err)
    }
  }

  // msg
  _onmsg(msg) {
    this._pushMsg(msg)
    this.$emit(IMEvents.onmsg, msg)
  }
  _updateMsg(obj) {
    this._pushMsg(obj.msgs)
  }
  getHistoryMsgs(id) {
    if (this._lastMsgMap[id] && this._lastMsgMap[id].hasLast) return;
    this.$emit(IMEvents.messageLoading, { to: id, loading: true })
    this._nim.getHistoryMsgs({
      scene: this._idToScene(id),
      to: id,
      asc: true,
      beginTime: this._lastMsgMap[id] ? this._lastMsgMap[id].beginTime : 0,
      reverse: this._lastMsgMap[id] ? false : true,
      done: (err, obj) => {
        this.$emit(IMEvents.messageLoading, { to: id, loading: false })
        if (!err) {
          this._pushMsg(obj.msgs)
          this._lastMsgMap[id] = {
            beginTime: obj.msgs[0].time,
            hasLast: obj.msgs.length < 100
          }
        }
      }
    })
  }
  _pushMsg(msgs) {
    if (!Array.isArray(msgs)) msgs = [msgs];
    const to = msgs[0].target;
    this._msgMap = this._msgMap || {}
    this._msgMap[to] = this._nim.mergeMsgs(this._msgMap[to], msgs)
    this.$emit(IMEvents.messageUpdated, { msgMap: cloneDeep(this._msgMap), to })
  }
  // msg - send
  sendMsg(msg, type) {
    switch(type) {
      case 'image':
        this._sendImgMsg(msg)
        break
      case 'audio':
        this._sendAudioMsg(msg)
        break;
      case 'route':
        this._sendCustomMsg(type, msg)
        break;
      case 'text':
      default:
        this._sendText(msg)
        break;
    }
  }
  _sendText(msg) {
    this._nim.sendText({
      scene: this._idToScene(msg.to),
      to: msg.to,
      text: msg.text,
      custom: JSON.stringify({
        createtime: msg.createtime,
      }),
      done: (e, m) => this._sendDone(e, m, { ...msg, type: 'text' }),
    })
  }
  _sendImgMsg(info) {
    this._nim.sendFile({
      scene: this._idToScene(info.to),
      to: info.to,
      type: 'image',
      fileInput: info.file,
      custom: JSON.stringify({
        createtime: info.createtime
      }),
      uploaddone: (err) => this._uploaddone(err, { ...info, type: 'image' }),
      done: (e, m) => this._sendDone(e, m, { ...info, type: 'audio' }),
    })
  }
  _sendAudioMsg(info) {
    const { to, blob, duration, createtime } = info
    this._nim.sendFile({
      scene: this._idToScene(to),
      to,
      type: 'audio',
      blob,
      custom: JSON.stringify({
        createtime,
        duration,
      }),
      uploaddone: (err) => this._uploaddone(err, { ...info, type: 'audio' }),
      done: (e, m) => this._sendDone(e, m, { ...info, type: 'audio' })
    })
  }
  _sendCustomMsg(type, info) {
    let messageType = null;
    switch(type) {
      case 'route':
        messageType = 'ROUTE'
        break;
    }
    this._nim.sendCustomMsg({
      scene: this._idToScene(info.to),
      to: info.to,
      text: type,
      content: type,
      custom: JSON.stringify({
        messageType,
        createtime: info.createtime,
        extra: info.extra,
      }),
      done: (e, m) => this._sendDone(e, m, { ...info, type })
    })
  }
  _uploaddone(err, sendMsg) {
    if (err) {
      this.$emit(IMEvents.messageSendError, sendMsg)
    }
  }
  _sendDone(error, msg, sendMsg) {
    if (error) {
      this.$emit(IMEvents.messageSendError, sendMsg)
    } else {
      this.getHistoryMsgs(sendMsg.to)
      this._pushMsg(msg)
    }
  }
  _idToScene(to) {
    if (this._singleMap[to]) return 'p2p'
    else if (this._teamsMap[to]) return 'team'
  }

  // sessions
  _onsessions(session) {
    if(session?.lastMsg?.attach?.type == 'updateTeam') return
    this._sessionList = this._nim.mergeSessions(this._sessionList, session)
    this.$emit(IMEvents.updateSession, cloneDeep(this._sessionList))
  }
  resetSessionUnread(sessionId) {
    this._nim.resetSessionUnread(sessionId, (err, failId) => {
      if (err) console.log('Reset Session failed ', err, 'ID:', failId)
    })
  }

  _onsyncdone() {
    console.log('onsyncdone')
  }
}

let instance = null;

function getInstance() {
  if (instance) return instance
  throw Error('IMClient is null')
}
const initState$ = new BehaviorSubject(false)

function initIMClientInstance() {
  if (instance) return instance
  instance = new IMClient()
  initState$.next(true)
  return instance
}

export default {
  initIMClientInstance,
  getInstance,
  initState$,
}
